<script lang="ts">
    import Chart from 'chart.js/auto'
    import { onDestroy } from 'svelte/internal'
    import Zact from '../../_common/z/zact.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import type { ReportResponse } from '../../../modules/reportsApi/catalog/report-catalog'
    import { NUMERIC_COLUMN_TYPES, OCCURRENCES_VALUE_KEY } from './report-chart-defaults'
    import type { ReportChartType } from './report-chart-defaults'

    // Abstração genérica: qualquer relatório (ReportResponse) pode virar gráfico desde que informem qual
    // coluna é a dimensão (rótulo), qual é o valor e o tipo de gráfico. dimensionKey/valueKey/type chegam
    // como o valor PADRÃO (inferido em report-chart-defaults.ts) — o usuário pode trocar livremente pelos
    // selects do zact acima do gráfico, sem precisar mudar filtro/relatório.
    export let response: ReportResponse
    export let dimensionKey: string
    export let valueKey: string
    export let type: ReportChartType = 'bar'

    const PALETTE = ['#008FFB', '#379E75', '#FEB019', '#775DD0', '#6179F3', '#D0483E', '#9AA0A6']
    const TYPE_OPTIONS = [
        { value: 'bar', label: 'Barras' },
        { value: 'line', label: 'Linha' },
        { value: 'pie', label: 'Pizza' },
    ]

    let canvas: HTMLCanvasElement
    let chart: Chart
    let selectedDimensionKey: string
    let selectedValueKey: string
    let selectedType: ReportChartType
    let grouped = false

    // dimensão aceita qualquer coluna; valor só faz sentido pra colunas numéricas (texto/data não somam/contam
    // como grandeza) — por isso a lista é filtrada, com [Ocorrências] sempre disponível como opção fixa
    $: dimensionColumnOptions = (response?.columns || []).map((col) => ({ value: col.key, label: col.label }))
    $: valueColumnOptions = [
        { value: OCCURRENCES_VALUE_KEY, label: '[Ocorrências]' },
        ...(response?.columns || []).filter((col) => NUMERIC_COLUMN_TYPES.includes(col.type)).map((col) => ({ value: col.key, label: col.label })),
    ]

    // mantém a escolha do usuário entre recargas do MESMO relatório (filtro/período mudou, colunas continuam
    // as mesmas); só volta pro valor inferido quando a coluna escolhida não existe mais na resposta atual
    // (ex.: trocou de relatório) — por isso não reseta incondicionalmente a cada novo response
    $: resetIfInvalid(response, dimensionKey, valueKey, type)
    function resetIfInvalid(response: ReportResponse, dimensionKey: string, valueKey: string, type: ReportChartType) {
        const dimensionKeys = (response?.columns || []).map((col) => col.key)
        const numericKeys = (response?.columns || []).filter((col) => NUMERIC_COLUMN_TYPES.includes(col.type)).map((col) => col.key)
        if (!selectedDimensionKey || !dimensionKeys.includes(selectedDimensionKey)) selectedDimensionKey = dimensionKey
        if (!selectedValueKey || (selectedValueKey !== OCCURRENCES_VALUE_KEY && !numericKeys.includes(selectedValueKey))) selectedValueKey = valueKey
        if (!selectedType) selectedType = type
    }

    $: draw(response, selectedDimensionKey, selectedValueKey, selectedType, grouped, canvas)

    function draw(response: ReportResponse, dimensionKey: string, valueKey: string, type: ReportChartType, grouped: boolean, canvas: HTMLCanvasElement) {
        if (!canvas || !response || !dimensionKey || !valueKey) return
        chart?.destroy()

        let labels: string[]
        let data: number[]
        let valueLabel: string
        if (valueKey === OCCURRENCES_VALUE_KEY) {
            // [Ocorrências] já é uma contagem por dimensão — inerentemente agrupado, o toggle "Agrupar" não se aplica aqui
            const counts = new Map<string, number>()
            for (const row of response.rows) {
                const label = String(row[dimensionKey] ?? '')
                counts.set(label, (counts.get(label) || 0) + 1)
            }
            labels = [...counts.keys()]
            data = [...counts.values()]
            valueLabel = 'Ocorrências'
        } else if (grouped) {
            // soma os valores de cada linha, agrupados pelo valor da dimensão
            const sums = new Map<string, number>()
            for (const row of response.rows) {
                const label = String(row[dimensionKey] ?? '')
                sums.set(label, (sums.get(label) || 0) + (Number(row[valueKey]) || 0))
            }
            labels = [...sums.keys()]
            data = [...sums.values()]
            valueLabel = response.columns.find((col) => col.key === valueKey)?.label || valueKey
        } else {
            labels = response.rows.map((row) => String(row[dimensionKey] ?? ''))
            data = response.rows.map((row) => Number(row[valueKey]) || 0)
            valueLabel = response.columns.find((col) => col.key === valueKey)?.label || valueKey
        }

        chart = new Chart(canvas, {
            type,
            data: {
                labels,
                datasets: [
                    {
                        label: valueLabel,
                        data,
                        backgroundColor: type === 'pie' ? labels.map((_, i) => PALETTE[i % PALETTE.length]) : '#6179F3',
                        borderColor: type === 'line' ? '#6179F3' : 'transparent',
                        borderRadius: type === 'bar' ? 4 : 0,
                        fill: type === 'line',
                        tension: type === 'line' ? 0.3 : 0,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: type === 'pie' } },
            },
        })
    }

    onDestroy(() => chart?.destroy())
</script>

<Zframe>
    <Zact class="mb-3 h-print">
        <Zinput skin="soft" type="select" label="Tipo" options={TYPE_OPTIONS} bind:value={selectedType} />
        <Zinput skin="soft" type="select" label="Dimensão (rótulo)" options={dimensionColumnOptions} bind:value={selectedDimensionKey} />
        <Zinput skin="soft" type="select" label="Valor" options={valueColumnOptions} bind:value={selectedValueKey} />
        <Zinput skin="soft" type="toggle" label="Agrupar pela dimensão" bind:value={grouped} />
    </Zact>
    <div class="chart-box">
        <canvas bind:this={canvas}></canvas>
    </div>
</Zframe>

<style>
    .chart-box {
        position: relative;
        width: 100%;
        height: 380px;
    }
</style>
